import { IntakeData } from '../context/IntakeContext'
import { buildIntakeReport } from './buildIntakeReport'
import { APP_BASE_URL } from './submitIntake'
import { ReportEntry, ReportQuestion } from '../types/intakeReport'

const EMPTY = '_Niet ingevuld_'

/**
 * Markdown-versie van de intake, geoptimaliseerd om direct aan een AI
 * (Claude / Magic Patterns) te geven: vaste koppen, vraag → antwoord,
 * en bijlagen bij naam.
 */
export function generateIntakeMarkdown(
  data: IntakeData,
  uploadedFiles: Record<string, File> = {},
): string {
  const report = buildIntakeReport(data, uploadedFiles)
  const lines: string[] = []

  lines.push(`# Website intake — ${report.clientName}`)
  lines.push('')
  lines.push(`- **Datum:** ${report.generatedAt}`)
  lines.push(`- **Bron:** ${APP_BASE_URL}`)
  lines.push(
    '- **Platform:** WordPress + Elementor (WooCommerce bij webshops), gebouwd door Noord Digital',
  )
  lines.push('')
  lines.push(
    '> **Instructie voor AI:** Dit document bevat alle antwoorden van de klant uit de intake.',
  )
  lines.push(
    '> Gebruik het als briefing voor (1) het ontwerp en de opbouw van de website en (2) het SEO-onderzoek.',
  )
  lines.push(
    `> Vragen met ${EMPTY} zijn wel getoond maar niet beantwoord — maak daar geen aannames over, maar markeer ze als open punt.`,
  )
  lines.push(
    '> Vragen die niet in dit document staan, waren niet van toepassing op basis van eerdere keuzes.',
  )
  lines.push('')

  report.sections.forEach((section, index) => {
    lines.push(`## ${index + 1}. ${section.title}`)
    lines.push('')
    lines.push(`_${section.purpose}_`)
    lines.push('')
    section.entries.forEach((entry) => renderEntry(entry, lines))
    ensureBlankLine(lines)
  })

  lines.push('## Aangeleverde bestanden')
  lines.push('')
  if (report.files.length === 0) {
    lines.push('_Er zijn geen bestanden geüpload._')
  } else {
    lines.push('_Alle bestanden zitten als bijlage in de intake-mail._')
    lines.push('')
    report.files.forEach((f) => lines.push(`- **${f.label}:** ${f.fileName}`))
  }
  lines.push('')

  const open = collectOpenQuestions(report.sections.flatMap((s) => s.entries))
  lines.push('## Open punten')
  lines.push('')
  if (open.length === 0) {
    lines.push('_Alle getoonde vragen zijn beantwoord._')
  } else {
    open.forEach((question) => lines.push(`- ${question}`))
  }
  lines.push('')

  return lines.join('\n')
}

function renderEntry(entry: ReportEntry, lines: string[]) {
  if (entry.type === 'group') {
    ensureBlankLine(lines)
    lines.push(`### ${entry.title}`)
    lines.push('')
    entry.items.forEach((item) => renderQuestion(item, lines))
    lines.push('')
    return
  }
  if (entry.type === 'colors') {
    const value = entry.colors.length
      ? entry.colors.map((c) => `${c.name || 'Kleur'} \`${c.hex}\``).join(', ')
      : EMPTY
    lines.push(`- ${label(entry.question)} ${value}`)
    return
  }
  renderQuestion(entry, lines)
}

function renderQuestion(item: ReportQuestion, lines: string[]) {
  if (!item.answer) {
    lines.push(`- ${label(item.question)} ${EMPTY}`)
    return
  }
  if (item.answer.includes('\n')) {
    lines.push(`- ${label(item.question)}`)
    item.answer.split('\n').forEach((line) => lines.push(`  > ${line}`))
    return
  }
  lines.push(`- ${label(item.question)} ${item.answer}`)
}

/** "**Vraag?**" of "**Label:**" — nooit een dubbel leesteken */
function label(question: string): string {
  return /[?:!]$/.test(question.trim())
    ? `**${question.trim()}**`
    : `**${question.trim()}:**`
}

function ensureBlankLine(lines: string[]) {
  if (lines.length > 0 && lines[lines.length - 1] !== '') lines.push('')
}

function collectOpenQuestions(entries: ReportEntry[]): string[] {
  const open: string[] = []
  entries.forEach((entry) => {
    if (entry.type === 'group') {
      entry.items.forEach((item) => {
        if (!item.answer) open.push(`${entry.title}: ${item.question}`)
      })
    } else if (entry.type === 'colors') {
      if (entry.colors.length === 0) open.push(entry.question)
    } else if (!entry.answer) {
      open.push(entry.question)
    }
  })
  return open
}
